import { createWorker, collectCode } from '../util/createWorker';
import { collapseMarkers } from './collapseMarkers';
import { generateTimelineBarBuilders, ITimelineBarBuilder } from './timelineBarBuilder';
import { CameraMessage, CompoundMarkersMap, Marker, MinutesPerSegment, PolicyMap, Timeline } from 'types';

//
// terminology used:
//
// Bar
//   - solid rectangle of colour; can overlap other bars
//
// Segment
//   - a fragment of the timeline (default is 30-minutes of time)
//   - these are stacked vertically in the UI
//
// Marker
//   - An overlay on the timeline.
//   - E.g. the little red flags that appear above an alarm.
//

// HACK: The __assign from tslib gets clobbered during minification... and we provide our own __assign method in createWorker.
//       So make sure to use our __assign rather than Object.assign or the TypeScript syntatic sugar for spreading objects.
declare const __assign: <T>(obj: T, ...objs: T[]) => T;

export type Input = {
    messages: readonly CameraMessage[];
    autoTrackingPolicies: PolicyMap;
    utcDate: number; // the start of the target date, expressed in UTC
    utcToday: number; // the start of the current date, expressed in UTC
    minutesInDay: number; // number of minutes in the target date (1440 by default but daylight savings can change it)
    displayWidth: number;
    labelWidth: number;
    minutesPerSegment: MinutesPerSegment;
};

type MarkersMap = {
    [markerType: string]: Marker[][];
};


//
// This is a special function that must not be broken up into smaller functions
// because the web Worker needs to have all the source code in one place.
// (not entirely true... there are three ways to break up this function and still work:
// 1. Create a dedicated source bundle, which I think involves ejecting from
//    CRA and setting it up in Webpack. Ugh. Don't want to give up CRA.
// 2. use the importSource() feature in the Worker... but then we need to serve
//    that other source bundle (which is essentially the same as point #1).
// 3. Take the dependent functions and convert them to strings... then preprend
//    them to the javascript we use to build the web worker).
// So we've gone with #3 and it seems to be working well.
// Dependencies are pulled in from separate source files and passed to the
// createWorker() call.
//
export const timelineWorker = (
    id: string,
    _collapseMarkers: typeof collapseMarkers,
    _generateTimelineBarBuilders: typeof generateTimelineBarBuilders,
) => {
    //console.log(`timeline[${id}]: Starting`);

    const oneMinute = 60 * 1000;
    const oneDay = 24 * 60 * oneMinute;

    // Precondition: markers are sorted in ascending order by time
    function divideIntoSegments(markers: Marker[], scale: number, segmentCount: number, minutesPerSegment: number) {
        const result = Array.apply(null, Array(segmentCount)).map((_: {}) => []) as Marker[][];
        let m = 0;
        for (let s = 0; s < segmentCount; s++) {
            const nextSegment = (s + 1) * minutesPerSegment * oneMinute * scale;

            while (markers.length > m && markers[m].position < nextSegment) {
                result[s].push(markers[m++]);
            }
        }
        return result;
    }

    function sortByZIndexAndTime(a: ITimelineBarBuilder, b: ITimelineBarBuilder) {
        if (a.zIndex < b.zIndex) return -1;
        if (a.zIndex > b.zIndex) return 1;
        if (a.start.t < b.start.t) return -1;
        if (a.start.t > b.start.t) return 1;
        if (a.end.t < b.end.t) return 1;
        if (a.end.t > b.end.t) return -1;
        return 0;
    }

    // Helper function to go back up to 24 hours but on a boundary that is a multiple
    // of the segment size -- so all the segments can start on a clean boundary
    function calculate24ishHoursAgo(time: number, minutesPerSegment: number) {
        const yesterday = time - oneDay;
        const remainder = yesterday % (minutesPerSegment * oneMinute);
        return yesterday + (remainder ? (minutesPerSegment * oneMinute) - remainder : 0);
    }

    function separateMarkersByType(markers: Marker[]) {
        return markers.reduce((result, m) => {
            const markersOfType = result[m.type] || [];
            return __assign({}, result, {
                [m.type]: [ ...markersOfType, m ],
            });
        }, {});
    }

    onmessage = (e: MessageEvent) => {
        //console.log(`timeline[${id}]: Data received`);
        //const start = performance.now();

        const timeline = generateTimeline(e);

        //const duration = performance.now() - start;
        //console.log(`timeline[${id}]: Result in ${Math.round(duration)}ms`);

        (postMessage as (msg: Timeline) => void)(timeline);
    };

    function generateTimeline(e: MessageEvent): Timeline {
        const { messages, autoTrackingPolicies, utcDate, utcToday, minutesInDay, displayWidth, labelWidth, minutesPerSegment } = e.data as Input;

        // Calculate baseTime to be either (24 hours + 1 segment) ago for live stream; or start of day for historical stream
        const baseTime =
            utcDate >= utcToday
                ? calculate24ishHoursAgo(Date.now(), minutesPerSegment)
                : utcDate;

        // Calculate # of segments based on minutes per segment
        const segments = Math.ceil(minutesInDay / minutesPerSegment);

        // Calculate displayHeight
        const segmentHeight = 12;
        const segmentMargin = 8;
        const displayHeight = (segmentHeight + segmentMargin) * segments + segmentMargin;

        const rightMargin = 5;
        const segmentWidth = displayWidth - labelWidth - rightMargin;

        // Calculate how many pixels per millisecond
        const scale = segmentWidth / (minutesPerSegment * oneMinute);

        // Heuristic function to determine how big an alarm marker will be.
        // The 'text' that is passed in is the number of alarms compounded into a single marker.
        // Single digit entries are enlarged a little. Double digit entries are expanded a little more.
        // Triple-digit will be tight but will fit in the current space for double.
        // Not expecting four and more digits.
        const getTextWidth = (text: string) => {
            if (text === '0') return 0;
            if (parseInt(text, 10) < 10) return 4;
            return 8;
        };

        const barBuilders = _generateTimelineBarBuilders(messages || [], autoTrackingPolicies).sort(sortByZIndexAndTime);
        const bars = barBuilders.map(b => b.toBar(baseTime, scale, segmentHeight));
        const allMarkers = barBuilders.map(b => b.getMarker(baseTime, scale)).filter(b => !!b) as Marker[];

        const markersByType = separateMarkersByType(allMarkers);

        // For each marker type, divide the marker arrays into segments
        const dividedMarkers = ['Alarm', 'AccessGranted', 'AccessDenied', 'AccessOther'].reduce((cm, markerType) => __assign({}, cm, {
            [markerType]: divideIntoSegments(markersByType[markerType] || [], scale, segments, minutesPerSegment),
        }), {} as MarkersMap);

        // For each marker type and for each segment, collapse markers of that type within that segment
        const compoundMarkers = Object.keys(dividedMarkers).reduce((cm, markerType) => __assign({}, cm, {
            [markerType]: dividedMarkers[markerType].map(markers => _collapseMarkers({
                markers,
                markerLeftPadding: 5,
                markerRightPadding: 5,
                getTextWidth,
            })),
        }), {} as CompoundMarkersMap);

        return {
            baseTime,
            displayWidth,
            displayHeight,
            segmentWidth,
            segmentHeight,
            segmentMargin,
            labelWidth,
            minutesPerSegment,
            segments,
            scale,
            bars,
            markers: compoundMarkers,
        };
    }
};

export const generateCode = (workerId: string) =>
    collectCode(workerId, timelineWorker, [
        collapseMarkers,
        generateTimelineBarBuilders,
    ])
;

export const factory = (workerId: string) =>
    createWorker(
        generateCode(workerId)
    )
;


// WEBPACK FOOTER //
// ./src/workers/timeline/timelineWorker.ts